// src/components/IndexFamilyRow.js
//
// Consumes the dedicated indices_etfs Go service at :6062/api/families.
// The service handles universe seed, .SA suffix logic, currency inference,
// and cross-listing groupings — the frontend just renders.

import React, { useCallback, useEffect, useRef, useState } from 'react';

const INDICES_API = '/api/history';

const C = {
  bg: '#07070a', card: '#111118',
  border: '#1e1e2a',
  gold: '#c9a227', purple: '#6660fe',
  red: '#f03e3e', green: '#34d399',
  amber: '#fbbf24', cyan: '#22d3ee',
  white: '#e8e8f0',
  dim: 'rgba(232,232,240,0.75)',
  dimmer: 'rgba(232,232,240,0.60)',
  faint: 'rgba(232,232,240,0.35)',
};

const FONT_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:ital,wght@0,400;0,600;0,700;1,400&family=Fraunces:ital,wght@0,600;0,700;1,600&display=swap');
`;

const NO_DECIMAL = new Set(['JPY','KRW','IDR','CLP','COP','HUF','TWD','VND']);

const fmtPrice = (v, currency) => {
  if (v == null || isNaN(v) || v === 0) return '—';
  const dp = NO_DECIMAL.has(currency) ? 0 : 2;
  return Number(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
};

const fmtVar = (v) => {
  if (v == null || isNaN(v)) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
};

const currencyChip = (currency) => {
  const map = { BRL:'R$', USD:'US$', EUR:'€', GBP:'£', JPY:'¥', CNY:'¥', INR:'₹', MXN:'$', CAD:'C$', HKD:'HK$', AUD:'A$', KRW:'₩', TWD:'NT$', SGD:'S$', IDR:'Rp', MYR:'RM' };
  return map[currency] || currency || '';
};

const SEEN_KEY = 'rr_index_family_seen_v2';
function getSeen() {
  try { return JSON.parse(sessionStorage.getItem(SEEN_KEY)) || []; }
  catch { return []; }
}
function pushSeen(id, maxSize) {
  const cur = getSeen();
  sessionStorage.setItem(SEEN_KEY, JSON.stringify(
    [id, ...cur.filter(x => x !== id)].slice(0, maxSize)
  ));
}

function useFamilies() {
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(() => {
    fetch(`${INDICES_API}/api/families`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`http ${r.status}`)))
      .then(data => {
        setFamilies(data.families || []);
        setLastRefresh(data.last_refresh ? new Date(data.last_refresh) : new Date());
        setError(null);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 180 * 1000);
    return () => clearInterval(t);
  }, [load]);

  return { families, loading, error, lastRefresh, reload: load };
}

const RoleTag = ({ role }) => {
  const styles = {
    'INDEX':      { bg: 'rgba(201,162,39,0.12)',  color: C.gold,   border: 'rgba(201,162,39,0.35)' },
    'ETF-BR':     { bg: 'rgba(52,211,153,0.10)',  color: C.green,  border: 'rgba(52,211,153,0.30)' },
    'ETF-US':     { bg: 'rgba(102,96,254,0.12)',  color: C.purple, border: 'rgba(102,96,254,0.35)' },
    'ETF-SECTOR': { bg: 'rgba(34,211,238,0.10)',  color: C.cyan,   border: 'rgba(34,211,238,0.30)' },
  };
  const s = styles[role] || styles['INDEX'];
  return (
    <span style={{
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: 9, fontWeight: 700, letterSpacing: '0.14em',
      background: s.bg, color: s.color,
      border: `1px solid ${s.border}`,
      padding: '3px 8px', borderRadius: 3,
      whiteSpace: 'nowrap',
    }}>{role}</span>
  );
};

const SourceDot = ({ source, error }) => {
  const map = {
    yahoo_v2: { color: C.green, label: 'YAHOO' },
    error:    { color: C.red,   label: 'ERRO' },
  };
  const s = map[source] || { color: C.faint, label: '—' };
  return (
    <span title={error || ''} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: 8, fontWeight: 700, color: s.color, letterSpacing: '0.12em',
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.color }} />
      {s.label}
    </span>
  );
};

const MemberBlock = ({ member, indexChange, isPrimary }) => {
  const hasPrice = member.ult != null && member.ult !== 0 && member.source === 'yahoo_v2';
  const isPos = (member.var ?? 0) >= 0;
  const varColor = isPos ? C.green : C.red;
  const spread = (indexChange != null && member.var != null && member.class !== 'INDEX')
    ? member.var - indexChange
    : null;

  return (
    <div style={{
      padding: isPrimary ? '18px 20px' : '14px 16px',
      background: isPrimary ? 'rgba(102,96,254,0.05)' : 'rgba(255,255,255,0.02)',
      border: `1px solid ${isPrimary ? 'rgba(102,96,254,0.25)' : C.border}`,
      borderRadius: 6,
      display: 'flex', flexDirection: 'column', gap: 8,
      opacity: hasPrice ? 1 : 0.5,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <RoleTag role={member.class} />
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: isPrimary ? 13 : 12, fontWeight: 700,
          color: C.white, letterSpacing: '0.02em',
        }}>{member.ticker}</span>
        <SourceDot source={member.source} error={member.error} />
        {spread != null && (
          <span title="Spread vs índice (ADR / cross-listing divergence)" style={{
            marginLeft: 'auto',
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
            padding: '2px 6px', borderRadius: 3,
            background: Math.abs(spread) > 0.5 ? 'rgba(201,162,39,0.15)' : 'rgba(148,163,184,0.08)',
            color: Math.abs(spread) > 0.5 ? C.gold : C.dimmer,
            border: `1px solid ${Math.abs(spread) > 0.5 ? 'rgba(201,162,39,0.35)' : 'rgba(148,163,184,0.20)'}`,
          }}>
            Δ {spread >= 0 ? '+' : ''}{spread.toFixed(2)}
          </span>
        )}
      </div>

      <div style={{
        fontFamily: "'Fraunces', Georgia, serif",
        fontSize: isPrimary ? 13 : 11.5, fontStyle: 'italic',
        color: C.dim, lineHeight: 1.3,
      }}>
        {member.name}
        {member.exchange && (
          <span style={{ color: C.dimmer, fontSize: 10 }}> · {member.exchange}</span>
        )}
      </div>

      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 12, marginTop: 4,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9, fontWeight: 700, color: C.dimmer, letterSpacing: '0.06em',
          }}>{currencyChip(member.currency)}</span>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: isPrimary ? 28 : 20, fontWeight: 700,
            color: C.white, letterSpacing: '-0.02em', lineHeight: 1,
          }}>
            {fmtPrice(member.ult, member.currency)}
          </span>
        </div>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: isPrimary ? 18 : 14, fontWeight: 700,
          color: hasPrice ? varColor : C.faint, letterSpacing: '-0.01em',
        }}>
          {hasPrice ? `${isPos ? '▲' : '▼'} ${fmtVar(member.var)}` : '—'}
        </span>
      </div>
    </div>
  );
};

const FamilyHeroCard = ({ family, onReroll, current, total }) => {
  const members = family.members || [];
  const indexMember = members.find(m => m.class === 'INDEX');
  const indexChange = indexMember?.var ?? null;
  const headline = indexMember || members.find(m => m.source === 'yahoo_v2') || members[0];
  const isPos = headline && (headline.var ?? 0) >= 0;
  const accentColor = isPos ? C.green : C.red;

  return (
    <div style={{
      background: C.card,
      borderRadius: 16,
      border: `1px solid ${C.border}`,
      overflow: 'hidden',
      fontFamily: "'IBM Plex Mono', monospace",
      color: C.white,
    }}>
      <div style={{
        position: 'relative',
        height: 260,
        overflow: 'hidden',
        background: `url(${family.hero_image}) center/cover`,
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(180deg, rgba(7,7,10,0.55) 0%, rgba(7,7,10,0.35) 45%, rgba(7,7,10,0.98) 100%)`,
        }} />

        {family.flag && family.flag !== 'un' && (
          <div style={{
            position: 'absolute',
            right: -60, bottom: -40,
            pointerEvents: 'none',
            opacity: 0.22,
            transform: 'rotate(-12deg)',
            filter: 'grayscale(0.7)',
          }}>
            <img
              src={`https://flagcdn.com/w640/${family.flag}.png`}
              alt=""
              style={{ width: 380, height: 'auto', display: 'block' }}
            />
          </div>
        )}
        {family.flag === 'un' && (
          <div style={{
            position: 'absolute', right: -20, bottom: -30,
            fontSize: 240, opacity: 0.12, pointerEvents: 'none',
          }}>🌐</div>
        )}

        <div style={{
          position: 'absolute', top: 20, left: 24,
          display: 'flex', alignItems: 'center', gap: 12, zIndex: 2,
        }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11, fontWeight: 800,
            color: C.purple,
            background: 'rgba(102,96,254,0.15)',
            border: '1px solid rgba(102,96,254,0.4)',
            padding: '4px 10px', borderRadius: 3,
            letterSpacing: '0.18em',
          }}>RR MAGMA</span>
          <span style={{
            fontSize: 9, fontWeight: 700, color: C.gold,
            background: 'rgba(201,162,39,0.12)',
            border: '1px solid rgba(201,162,39,0.3)',
            padding: '3px 8px', borderRadius: 3,
            letterSpacing: '0.14em',
          }}>MERCADO GLOBAL · ÍNDICE</span>
        </div>

        <div style={{
          position: 'absolute', top: 20, right: 24, zIndex: 2,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9, fontWeight: 700, color: C.dimmer,
            letterSpacing: '0.14em',
          }}>{String(current + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
          <button
            className="rr-idx-reroll"
            onClick={onReroll}
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 4, color: C.white,
              fontSize: 9, fontWeight: 700,
              padding: '6px 12px', cursor: 'pointer',
              fontFamily: 'inherit', letterSpacing: '0.14em',
              display: 'flex', alignItems: 'center', gap: 6,
              backdropFilter: 'blur(8px)', transition: 'all 0.15s',
            }}
          ><span>↻</span> PRÓXIMO</button>
        </div>

        <div style={{
          position: 'absolute', bottom: 22, left: 24, right: 24, zIndex: 2,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: 20, flexWrap: 'wrap',
        }}>
          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <div style={{
              fontSize: 9, fontWeight: 700, color: accentColor,
              letterSpacing: '0.16em', marginBottom: 8,
              textShadow: '0 2px 8px rgba(0,0,0,0.6)',
            }}>
              ÚLTIMO FECHAMENTO · {members.length} VEÍCULOS
            </div>
            <h2 style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: 40, fontWeight: 700, color: C.white,
              margin: 0, lineHeight: 1.05, letterSpacing: '-0.02em',
              textShadow: '0 2px 12px rgba(0,0,0,0.7)',
            }}>{family.label}</h2>
            <div style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontStyle: 'italic', fontSize: 14, color: C.dim,
              marginTop: 6, textShadow: '0 1px 6px rgba(0,0,0,0.7)',
            }}>{family.subtitle}</div>
          </div>
          {headline && headline.source === 'yahoo_v2' && (
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 44, fontWeight: 800, color: accentColor,
                lineHeight: 1, letterSpacing: '-0.03em',
                textShadow: '0 2px 12px rgba(0,0,0,0.7)',
              }}>
                {fmtVar(headline.var)}
              </div>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10, color: C.dimmer, letterSpacing: '0.14em',
                marginTop: 4,
              }}>VARIAÇÃO · {isPos ? 'ALTA' : 'BAIXA'}</div>
            </div>
          )}
        </div>
      </div>

      <div style={{
        padding: '14px 24px',
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{
          fontFamily: "'Fraunces', Georgia, serif",
          fontStyle: 'italic', fontSize: 12, color: C.dim,
          lineHeight: 1.4,
        }}>
          Índice de referência e seus <em>wrappers</em> em ETFs listados em bolsas diferentes.
          A pílula <strong style={{ color: C.gold }}>Δ</strong> mede o spread do ETF contra o índice —
          leitura de divergência ADR / cross-listing.
        </div>
      </div>

      <div style={{
        padding: '20px 24px 22px',
        display: 'grid',
        gridTemplateColumns: members.length >= 3 ? 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))' : '1fr',
        gap: 12,
      }}>
        {members.length === 0 ? (
          <div style={{
            padding: 30, textAlign: 'center',
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11, color: C.dimmer,
          }}>Nenhum veículo carregado para esta família.</div>
        ) : members.map(m => (
          <MemberBlock
            key={m.ticker}
            member={m}
            indexChange={indexChange}
            isPrimary={m.class === 'INDEX'}
          />
        ))}
      </div>

      <div style={{
        padding: '14px 22px 18px',
        borderTop: `1px solid ${C.border}`,
      }}>
        <div style={{
          padding: '7px 11px',
          background: 'rgba(251,191,36,0.04)',
          border: '1px solid rgba(251,191,36,0.08)',
          borderRadius: 4, marginBottom: 12,
        }}>
          <p style={{
            fontSize: 8, color: 'rgba(251,191,36,0.55)',
            lineHeight: 1.4, margin: 0, fontFamily: 'inherit',
          }}>
            <strong style={{ color: 'rgba(251,191,36,0.75)' }}>⚠ FONTE:</strong>{' '}
            Serviço indices_etfs · Yahoo v2 proxy (cache incremental) · último fechamento.
            Não constitui recomendação de investimento.
          </p>
        </div>
        <button
          onClick={onReroll}
          style={{
            display: 'block', width: '100%',
            padding: '12px 0', textAlign: 'center',
            background: C.purple, border: `1px solid ${C.purple}`,
            borderRadius: 6, color: '#fff',
            fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
            cursor: 'pointer',
            fontFamily: "'IBM Plex Mono', monospace",
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#7570ff'}
          onMouseLeave={e => e.currentTarget.style.background = C.purple}
        >▶ PRÓXIMA FAMÍLIA · CROSS-LISTING</button>
      </div>
    </div>
  );
};

const FamilyStrip = ({ families, activeIdx, onPick }) => (
  <div style={{
    marginTop: 20,
    display: 'flex', gap: 10, overflowX: 'auto',
    padding: '4px 2px 12px',
    scrollbarWidth: 'thin',
  }}>
    {families.map((f, i) => {
      const members = f.members || [];
      const first = members[0];
      const isActive = i === activeIdx;
      const isPos = first && (first.var ?? 0) >= 0;
      return (
        <button
          key={f.id}
          onClick={() => onPick(i)}
          style={{
            flexShrink: 0,
            minWidth: 180,
            padding: '10px 12px',
            background: isActive ? 'rgba(102,96,254,0.10)' : C.card,
            border: `1px solid ${isActive ? 'rgba(102,96,254,0.4)' : C.border}`,
            borderRadius: 8,
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: "'IBM Plex Mono', monospace",
            color: C.white,
            transition: 'all 0.15s',
            display: 'flex', flexDirection: 'column', gap: 6,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {f.flag && f.flag !== 'un' && (
              <img
                src={`https://flagcdn.com/w40/${f.flag}.png`}
                alt=""
                style={{ width: 20, height: 'auto', borderRadius: 2, flexShrink: 0 }}
              />
            )}
            {f.flag === 'un' && <span style={{ fontSize: 16 }}>🌐</span>}
            <span style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: 12, color: C.white, fontWeight: 600,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{f.label}</span>
          </div>
          <div style={{
            fontSize: 9, color: C.dimmer, letterSpacing: '0.1em',
          }}>{members.length} VEÍCULOS{first ? ` · ${first.ticker}` : ''}</div>
          {first && first.source === 'yahoo_v2' && (
            <div style={{
              fontSize: 11, fontWeight: 700,
              color: isPos ? C.green : C.red,
            }}>{fmtVar(first.var)}</div>
          )}
        </button>
      );
    })}
  </div>
);

export const IndexFamilyRow = ({ theme }) => {
  const { families, loading, error, lastRefresh } = useFamilies();
  const [activeIdx, setActiveIdx] = useState(0);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current || loading || families.length === 0) return;
    const seen = getSeen();
    const firstUnseen = families.findIndex(f => !seen.includes(f.id));
    if (firstUnseen >= 0) setActiveIdx(firstUnseen);
    initialized.current = true;
  }, [loading, families]);

  const active = families[activeIdx];

  const nextFamily = useCallback(() => {
    setActiveIdx(i => {
      const next = (i + 1) % families.length;
      if (families[i]) pushSeen(families[i].id, families.length - 1);
      return next;
    });
  }, [families]);

  const pickFamily = useCallback((idx) => {
    setActiveIdx(idx);
    if (families[idx]) pushSeen(families[idx].id, families.length - 1);
  }, [families]);

  return (
    <div style={{ marginBottom: '4rem' }}>
      <style>{FONT_IMPORT}{`
        .rr-idx-reroll:hover { background: rgba(255,255,255,0.16) !important; }
        .rr-idx-reroll:active { transform: scale(0.97); }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <h2 style={{
          fontFamily: "'Fraunces', Georgia, serif",
          fontSize: '2rem', fontWeight: 700, margin: 0,
          color: theme?.textPrimary || C.white,
          letterSpacing: '-0.01em',
        }}>
          Índices por Família
        </h2>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 9, fontWeight: 700, letterSpacing: '0.14em',
          background: 'rgba(102,96,254,0.12)', color: C.purple,
          border: '1px solid rgba(102,96,254,0.35)',
          padding: '4px 10px', borderRadius: 3,
        }}>
          CROSS-LISTING · ADR ARBITRAGE
        </span>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 9, fontWeight: 700, letterSpacing: '0.14em',
          background: 'rgba(52,211,153,0.10)', color: C.green,
          border: '1px solid rgba(52,211,153,0.30)',
          padding: '4px 10px', borderRadius: 3,
        }}>
          :6062 · indices_etfs
        </span>
        {lastRefresh && (
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9, color: C.dimmer, letterSpacing: '0.1em',
            marginLeft: 'auto',
          }}>ATUALIZADO {lastRefresh.toLocaleTimeString('pt-BR')}</span>
        )}
      </div>

      <p style={{
        fontFamily: "'Fraunces', Georgia, serif", fontStyle: 'italic',
        color: theme?.textSecondary || C.dim,
        marginBottom: '1.5rem', maxWidth: 820, fontSize: 14,
        lineHeight: 1.5,
      }}>
        O mesmo mercado, vestido em três roupas: índice de referência,
        ETF local e ETF listado nos EUA. Cotações sempre populadas com o último
        fechamento — funciona nos finais de semana e feriados.
      </p>

      {loading ? (
        <div style={{
          padding: 60, textAlign: 'center',
          background: C.card, borderRadius: 16,
          border: `1px solid ${C.border}`,
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11, color: C.dimmer,
        }}>
          Carregando famílias do serviço indices_etfs...
        </div>
      ) : error ? (
        <div style={{
          padding: 30, textAlign: 'center',
          background: 'rgba(240,62,62,0.05)',
          border: '1px solid rgba(240,62,62,0.25)',
          borderRadius: 12, color: C.red,
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
        }}>
          ⚠ indices_etfs offline ({error}). Ligue o serviço: <code style={{color: C.gold}}>go build -o indices_etfs . && ./indices_etfs</code>
        </div>
      ) : active ? (
        <>
          <FamilyHeroCard
            key={active.id}
            family={active}
            onReroll={nextFamily}
            current={activeIdx}
            total={families.length}
          />
          <FamilyStrip
            families={families}
            activeIdx={activeIdx}
            onPick={pickFamily}
          />
        </>
      ) : null}
    </div>
  );
};

export default IndexFamilyRow;